import type React from "react";
import {
    useMemo,
    useEffect,
    useRef,
    type Dispatch,
    type SetStateAction,
    useState,
} from "react";
import Spinner from "../../misc/Spinner";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../../listings/slideshow/Tooltip";
import { FaCheck } from "react-icons/fa6";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { Button } from "../../misc/Button";
import { cn } from "../../../utils/classMerger";
import CustomToast from "../../misc/CustomToast";
import type {
    MediaSelectorProps,
    UnifiedMedia,
} from "../../listings/slideshow/types";
import { toast } from "react-toastify";
import { useIsMobile } from "../../../hooks/useIsMobile";
import type { Assets, AssetType } from "../../../types/asset-type";
import type { ArchiveMedia } from "../types";
import AssetsUploader from "../../misc/AssetsUploader";
import { promotionTypes, ResourceType } from "../../content/types";
import CustomSelect from "../../misc/CustomSelect";
import { Badge } from "../../calendar/Badge";
import type { PromotionType } from "../../promotions/types";
import type { ListingType } from "../../../types/userListing";
import { SocialPlatforms } from "../../campaign/types";
import { isPromotionType } from "../../promotions/util";

interface EnhancedMediaSelectorProps extends MediaSelectorProps {
    showAssetsUploader?: boolean;
    onAssetsUploaded?: () => void;
    listingId?: string;
    listingType?: ListingType | undefined;
    hasVideoSelected: boolean;
    hasImageSelected: boolean;
    setSelectedMedia: (media: string[]) => void;
    activeResourceType: ResourceType | null;
    setActiveResourceType: Dispatch<SetStateAction<ResourceType | null>>;
    activePromotionType: PromotionType | null;
    setActivePromotionType: Dispatch<SetStateAction<PromotionType | null>>;
    setActiveTab?: (
        tab: "images" | "videos" | "promotionImages" | "assets"
    ) => void;
    uploadsEnabled?: boolean;
    isVideo?: boolean;
    platform?: string;
    postType?: string;
    onPendingFilesChange?: (count: number) => void;
    onActiveUploadsChange?: (count: number) => void;
    sharedAssetIds?: Set<string>;
    isLoading?: boolean;
    // When set, restricts the Shared Media resource-type dropdown to a single side.
    mediaConstraint?: "image" | "video";
    // The parent renders its own media-type control (segmented Images / Video,
    // MB-1492/MB-1496): hides the inner heading and the resource-type dropdown.
    hideTypeControls?: boolean;
}

const PostMediaSelector: React.FC<EnhancedMediaSelectorProps> = ({
    images = [],
    videos = [],
    promotionImages = [],
    assets = [],
    archived = [],
    selectedMedia,
    onSelect,
    maxMedia,
    minMedia,
    showAssetsUploader = false,
    onAssetsUploaded,
    listingId,
    listingType,
    hasVideoSelected,
    hasImageSelected,
    activeTab,
    setSelectedMedia,
    type,
    activeResourceType,
    setActiveResourceType,
    activePromotionType,
    setActivePromotionType,
    setActiveTab,
    uploadsEnabled = false,
    isVideo = false,
    platform,
    postType,
    onPendingFilesChange,
    onActiveUploadsChange,
    sharedAssetIds,
    isLoading = false,
    mediaConstraint,
    hideTypeControls = false,
}) => {
    const isMobile = useIsMobile(950);

    const [uploadOccurred, setUploadOccurred] = useState(false);

    const ITEMS_PER_PAGE = isMobile ? 4 : 10;
    const [currentPage, setCurrentPage] = useState(1);

    const mapAssetsToUnifiedMedia = (asset: Assets): UnifiedMedia => ({
        ...asset,
        mediaType:
            asset.assetType === "Video"
                ? "video"
                : asset.assetType === "Logo"
                ? "logo"
                : "image",
    });

    const mapArchiveToUnifiedMedia = (archive: ArchiveMedia): UnifiedMedia => ({
        ...archive,
        mediaType: archive.type === "Removed Video" ? "video" : "image",
    });

    const mapPromotionToUnifiedMedia = (promotion: any): UnifiedMedia => ({
        ...promotion,
        mediaType: "image",
    });

    const combinedMedia: UnifiedMedia[] = useMemo(() => {
        if (activeTab === "promotionImages") {
            let mediaList = promotionImages.map((pi) =>
                mapPromotionToUnifiedMedia(pi)
            );
            if (activePromotionType) {
                mediaList = mediaList.filter(
                    (item) => (item as any).assetType === activePromotionType
                );
            }
            return mediaList;
        } else if (activeTab === "assets") {
            let mediaList = assets.map((a) => mapAssetsToUnifiedMedia(a));
            if (activeResourceType) {
                mediaList = mediaList.filter((item) => {
                    if (
                        item.mediaType === "image" &&
                        activeResourceType === ResourceType.Logo
                    ) {
                        return (item as Assets).assetType === ResourceType.Logo;
                    }
                    return item.mediaType === activeResourceType.toLowerCase();
                });
            }
            return mediaList;
        } else if (activeTab === "images") {
            const ownImages = images.map(
                (i) => ({ ...i, mediaType: "image" } as UnifiedMedia)
            );
            return ownImages;
        } else if (activeTab === "videos") {
            const ownVideos = videos.map(
                (v) => ({ ...v, mediaType: "video" } as UnifiedMedia)
            );
            return ownVideos;
        } else if (activeTab === "archived") {
            const archivedMedia = archived.map((a) => mapArchiveToUnifiedMedia(a));
            return postType === "Promotion"
                ? archivedMedia.filter((item) => item.mediaType !== "video")
                : archivedMedia;
        }
        return [];
    }, [
        activeTab,
        promotionImages,
        activePromotionType,
        assets,
        activeResourceType,
        images,
        videos,
        archived,
        postType,
    ]);

    // Sort: shared assets first, then listing media
    const sortedCombinedMedia = useMemo(() => {
        if (!sharedAssetIds || sharedAssetIds.size === 0) return combinedMedia;
        return [...combinedMedia].sort((a, b) => {
            const aShared = sharedAssetIds.has(a.id) ? 0 : 1;
            const bShared = sharedAssetIds.has(b.id) ? 0 : 1;
            return aShared - bShared;
        });
    }, [combinedMedia, sharedAssetIds]);

    // Reset to page 1 whenever the media list changes (tab/filter switch, uploads)
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, activeResourceType, activePromotionType]);

    const totalPages = Math.ceil(sortedCombinedMedia.length / ITEMS_PER_PAGE);
    const paginatedMedia = sortedCombinedMedia.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const allMedia = useMemo(() => {
        return [
            ...images.map((i) => ({ ...i, mediaType: "image" })),
            ...videos.map((v) => ({ ...v, mediaType: "video" })),
            ...promotionImages.map((pi) => mapPromotionToUnifiedMedia(pi)),
            ...assets.map((a) => mapAssetsToUnifiedMedia(a)),
            ...archived.map((a) => mapArchiveToUnifiedMedia(a)),
        ];
    }, [images, videos, promotionImages, assets, archived]);

    const prevCombinedMediaRef = useRef<Assets[]>(assets);

    const assetImagesLength = useMemo(
        () =>
            assets.filter((item) => item.assetType === ResourceType.Image)
                .length,
        [assets]
    );
    const assetVideosLength = useMemo(
        () =>
            assets.filter((item) => item.assetType === ResourceType.Video)
                .length,
        [assets]
    );
    const assetLogosLength = useMemo(
        () =>
            assets.filter((item) => item.assetType === ResourceType.Logo)
                .length,
        [assets]
    );

    const promotionTypeOptions = useMemo(() => {
        if (uploadsEnabled) {
            return promotionTypes.map((type) => {
                const count =
                    promotionImages?.filter(
                        (promotionImage) =>
                            isPromotionType(promotionImage.assetType) &&
                            promotionImage.assetType === type
                    ).length || 0;
                return {
                    value: type,
                    label: `${
                        type.charAt(0).toUpperCase() +
                        type.slice(1).replace(/([A-Z])/g, " $1")
                    } (${count})`,
                };
            });
        } else {
            const types = new Set<string>();
            promotionImages.forEach((pi) => {
                if (pi.assetType) {
                    types.add(pi.assetType);
                }
            });
            return Array.from(types).map((t) => ({
                value: t as PromotionType,
                label:
                    t.charAt(0).toUpperCase() +
                    t.slice(1).replace(/([A-Z])/g, " $1"),
            }));
        }
    }, [uploadsEnabled, promotionImages]);

    useEffect(() => {
        if (!activeResourceType) {
            if (assetImagesLength > 0 && !isVideo) {
                setActiveResourceType(ResourceType.Image);
            } else if (
                assetVideosLength > 0 &&
                platform !== SocialPlatforms.Pinterest &&
                platform !== SocialPlatforms.Google &&
                postType !== "Promotion"
            ) {
                setActiveResourceType(ResourceType.Video);
            } else if (assetLogosLength > 0 && !isVideo) {
                setActiveResourceType(ResourceType.Logo);
            } else if (!isVideo) {
                setActiveResourceType(ResourceType.Image);
            } else {
                setActiveResourceType(null);
            }
        }
    }, [
        assetImagesLength,
        assetVideosLength,
        assetLogosLength,
        setActiveResourceType,
        activeResourceType,
        isVideo,
        platform,
        postType,
    ]);

    useEffect(() => {
        if (promotionTypeOptions.length > 0) {
            setActivePromotionType(promotionTypeOptions[0].value);
        } else {
            setActivePromotionType(null);
        }
    }, [promotionTypeOptions, setActivePromotionType]);

    const isImage = (item: UnifiedMedia): boolean =>
        item.mediaType === "image" || item.mediaType === "logo";
    const thisIsVideo = (item: UnifiedMedia): boolean =>
        item.mediaType === "video";

    const handleSelect = (item: UnifiedMedia) => {
        // De-selection is always allowed - a selection that is no longer legal
        // (e.g. a legacy mixed image+video post) must stay unwindable.
        if (selectedMedia.includes(item.id)) {
            onSelect(item.id);
            return;
        }
        if (thisIsVideo(item)) {
            if (hasImageSelected) {
                toast.error(
                    <CustomToast
                        message="You can select either one video or up to max images."
                        type="error"
                    />
                );
                return;
            }
            if (selectedMedia.includes(item.id)) {
                onSelect(item.id);
            } else if (selectedMedia.length === 0) {
                // No media selected yet — allow video.
                onSelect(item.id);
            } else {
                toast.info(
                    <CustomToast
                        message="Max video selected. Deselect the current video to select a new one."
                        type="error"
                    />
                );
            }
        } else if (isImage(item)) {
            if (hasVideoSelected) {
                toast.error(
                    <CustomToast
                        message="You can select either one video or up to max images."
                        type="error"
                    />
                );
                return;
            }
            if (
                !selectedMedia.includes(item.id) &&
                selectedMedia.length >= maxMedia &&
                maxMedia !== 1
            ) {
                toast.error(
                    <CustomToast
                        message={`You can select up to ${maxMedia} images.`}
                        type="error"
                    />
                );
                return;
            }
            onSelect(item.id);
        }
    };

    const isSelectionDisabled = (item: UnifiedMedia): boolean => {
        if (selectedMedia.includes(item.id)) return false;
        if (item.mediaType === "video" && hasVideoSelected && !selectedMedia.includes(item.id)) {
            return true;
        }
        if (item.mediaType === "video" && hasImageSelected) {
            return true;
        }
        if (
            (item.mediaType === "image" || item.mediaType === "logo") &&
            hasVideoSelected
        ) {
            return true;
        }
        if (
            (item.mediaType === "image" || item.mediaType === "logo") &&
            !hasVideoSelected &&
            selectedMedia.length >= maxMedia &&
            !selectedMedia.includes(item.id) &&
            maxMedia !== 1
        ) {
            return true;
        }
        return false;
    };

    const imageCountText = (count: number) =>
        `${count} image${count !== 1 ? "s" : ""}`;

    const getSelectedMediaText = () => {
        if (selectedMedia.length > 0) {
            return `Selected ${selectedMedia.length} / ${
                hasVideoSelected ? "1 video" : imageCountText(maxMedia)
            }`;
        }

        switch (activeTab) {
            case "images":
                return `Selected 0 / ${imageCountText(maxMedia)}`;
            case "videos":
                return "Selected 0 / 1 video";
            case "assets":
                return activeResourceType === ResourceType.Image ||
                    activeResourceType === ResourceType.Logo
                    ? `Selected 0 / ${imageCountText(maxMedia)}.`
                    : "Selected 0 / 1 Video";
            case "promotionImages":
                return `Selected 0 / ${imageCountText(maxMedia)}`;
            default:
                return "Select media";
        }
    };

    const handleAssetsUploadedWrapper = async () => {
        if (onAssetsUploaded) {
            // Switch tab immediately for instant UI feedback
            if (setActiveTab) {
                if (uploadsEnabled) {
                    if (type === "image") {
                        setActiveTab("images");
                    } else if (type === "video") {
                        setActiveTab("videos");
                    } else {
                        setActiveTab("assets");
                    }
                } else {
                    setActiveTab("assets");
                }
            }

            if (type === "assetVideo" || type === "video") {
                setActiveResourceType(ResourceType.Video);
            }

            // Load data in background after tab switch
            await onAssetsUploaded();
            setUploadOccurred(true);
        }
    };

    useEffect(() => {
        if (uploadOccurred) {
            const oldIds = new Set(
                prevCombinedMediaRef.current.map((item) => item.id)
            );

            const newMediaItems = assets.filter((item) => !oldIds.has(item.id));

            const newVideos = newMediaItems.filter(
                (item) => item.assetType === ResourceType.Video
            );

            const newImages = newMediaItems.filter(
                (item) =>
                    item.assetType === ResourceType.Image ||
                    item.assetType === ResourceType.Logo
            );

            // Mixed selections are retired (MB-1495): uploads follow the
            // single-type replace rules - uploading the other type replaces
            // the current selection instead of merging with it.
            {

                // Single-type mode (image / video) — existing replace logic

                // Check if we currently have any media selected
                const hasSelectedMedia = selectedMedia.length > 0;

                // Check if we have a video currently selected
                const hasVideoCurrentlySelected = selectedMedia.some((id) => {
                    const item = allMedia.find((media) => media.id === id);
                    return item && item.mediaType === "video";
                });

                // When no media is selected: Adding media will auto select that media
                if (!hasSelectedMedia) {
                    if (newVideos.length > 0) {
                        setActiveResourceType(ResourceType.Video);
                        setActiveTab && setActiveTab("assets");

                        setSelectedMedia([newVideos[0].id]);
                    } else if (newImages.length > 0) {
                        setActiveResourceType(
                            newImages[0].assetType === ResourceType.Logo
                                ? ResourceType.Logo
                                : ResourceType.Image
                        );
                        setActiveTab && setActiveTab("assets");

                        setSelectedMedia(
                            newImages.map((item) => item.id).slice(0, maxMedia)
                        );
                    }
                }
                // When video is selected:
                else if (hasVideoCurrentlySelected) {
                    if (newVideos.length > 0) {
                        setActiveResourceType(ResourceType.Video);
                        setActiveTab && setActiveTab("assets");
                        // Adding another video(s) will select the latest uploaded video
                        setSelectedMedia([newVideos[newVideos.length - 1].id]);
                    } else if (newImages.length > 0) {
                        setActiveResourceType(
                            newImages[0].assetType === ResourceType.Logo
                                ? ResourceType.Logo
                                : ResourceType.Image
                        );
                        setActiveTab && setActiveTab("assets");
                        // Adding images will deselect the video and will select the images
                        // giving priority to latest uploaded image up to the maxlimit
                        const sortedNewImages = [...newImages].reverse(); // Reverse to prioritize latest uploads
                        setSelectedMedia(
                            sortedNewImages
                                .map((item) => item.id)
                                .slice(0, maxMedia)
                        );
                    }
                }
                // When images are selected:
                else {
                    if (newVideos.length > 0) {
                        setActiveResourceType(ResourceType.Video);
                        setActiveTab && setActiveTab("assets");
                        // Adding a video(s) will deselect all previously selected images and select the latest uploaded video
                        setSelectedMedia([newVideos[newVideos.length - 1].id]);
                    } else if (newImages.length > 0) {
                        setActiveResourceType(
                            newImages[0].assetType === ResourceType.Logo
                                ? ResourceType.Logo
                                : ResourceType.Image
                        );
                        setActiveTab && setActiveTab("assets");
                        // Adding new images will preselect the new images
                        // If the images preselected and the new images cross the max count,
                        // first preference will be given to the newly added images (last image gets the first priority)
                        const sortedNewImages = [...newImages].reverse(); // Reverse to prioritize latest uploads
                        const newImageIds = sortedNewImages.map((item) => item.id);

                        if (selectedMedia.length + newImageIds.length <= maxMedia) {
                            // If we have room for all new images, add them all
                            setSelectedMedia([...newImageIds, ...selectedMedia]);
                        } else {
                            // If adding all would exceed max, prioritize new images
                            const remainingSlots = Math.max(
                                0,
                                maxMedia - newImageIds.length
                            );
                            const existingToKeep = selectedMedia.slice(
                                0,
                                remainingSlots
                            );
                            setSelectedMedia(
                                [...newImageIds, ...existingToKeep].slice(
                                    0,
                                    maxMedia
                                )
                            );
                        }
                    }
                }
            }

            setUploadOccurred(false);
        }
        prevCombinedMediaRef.current = assets;
    }, [
        assets,
        uploadOccurred,
        maxMedia,
        selectedMedia,
        setSelectedMedia,
        allMedia,
        setActiveResourceType,
        setActiveTab,
        type,
    ]);

    const options = isVideo || mediaConstraint === "video"
        ? [
              {
                  value: ResourceType.Video,
                  label: "Video",
              },
          ]
        : mediaConstraint === "image" ||
          platform === SocialPlatforms.Pinterest ||
          platform === SocialPlatforms.Google ||
          platform
              ?.split(",")
              ?.map((p) => p.trim().toLowerCase())
              ?.includes("pinterest") ||
          platform
              ?.split(",")
              ?.map((p) => p.trim().toLowerCase())
              ?.includes("google") ||
          platform
              ?.split(",")
              ?.map((p) => p.trim().toLowerCase())
              ?.includes("gmb") ||
          postType === "Promotion"
        ? [
              {
                  value: ResourceType.Image,
                  label: "Image",
              },
              {
                  value: ResourceType.Logo,
                  label: "Logo",
              },
          ]
        : [
              {
                  value: ResourceType.Image,
                  label: "Image",
              },
              {
                  value: ResourceType.Video,
                  label: "Video",
              },
              {
                  value: ResourceType.Logo,
                  label: "Logo",
              },
          ];

    const renderMediaItem = (item: UnifiedMedia) => (
        <TooltipProvider key={item.id}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className={cn(
                            "group relative w-full aspect-[4/3] cursor-pointer overflow-hidden rounded-lg border transition-transform duration-200",
                            selectedMedia.includes(item.id) && "ring-2 ring-primary",
                            !selectedMedia.includes(item.id) && isSelectionDisabled(item)
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                        )}
                        onClick={() => {
                            if (isSelectionDisabled(item)) {
                                toast.info(
                                    <CustomToast
                                        message={
                                            item.mediaType === "video"
                                                ? "Max video selected. Deselect the current video to select a new one."
                                                : `Max media selected (${maxMedia} images).`
                                        }
                                        type="error"
                                    />,
                                    { autoClose: 3000 }
                                );
                                return;
                            }
                            handleSelect(item);
                        }}
                    >
                        <Badge className="absolute top-1 right-1 z-40" variant="secondary">
                            <span className="text-[10px]">
                                {sharedAssetIds?.has(item.id)
                                    ? "SHARED"
                                    : postType === "Promotion"
                                    ? "PROMOTION"
                                    : "LISTING"}
                            </span>
                        </Badge>
                        {thisIsVideo(item) ? (
                            <div className="relative w-full h-full">
                                <img
                                    src={(item as any).thumbnail || item.signedUrl || item.url}
                                    alt="Selectable Media"
                                    loading="lazy"
                                    className="object-contain w-full h-full transition-transform group-hover:scale-105"
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded"></div>
                            </div>
                        ) : isImage(item) ? (
                            <img
                                src={item.signedUrl || item.url}
                                alt="Selectable Media"
                                loading="lazy"
                                className="object-contain w-full h-full transition-transform group-hover:scale-105"
                            />
                        ) : null}
                        {selectedMedia.includes(item.id) && (
                            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                <span className="absolute bottom-2 right-2 bg-primary text-white text-xs px-2 py-1 rounded">
                                    {selectedMedia.indexOf(item.id) + 1}
                                </span>
                                <FaCheck className="h-6 w-6 text-white" />
                            </div>
                        )}
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <span className="text-white">
                        {selectedMedia.includes(item.id) ? "Click to deselect" : "Click to select"}
                    </span>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    const renderPagination = (
        page: number,
        total: number,
        setPage: Dispatch<SetStateAction<number>>
    ) => (
        <div className="flex justify-between items-center mt-4">
            <Button
                variant="outline"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page <= 1}
            >
                <FaArrowLeft className={`${!isMobile && "mr-2"} h-4 w-4`} />
                {!isMobile && "Previous"}
            </Button>
            <span className="text-sm text-gray-600">
                {isMobile ? "" : "Page "}
                {total > 0 ? page : 0} of {total}
            </span>
            <Button
                variant="outline"
                onClick={() => setPage((p) => Math.min(p + 1, total))}
                disabled={page >= total || total === 0}
            >
                {!isMobile && "Next"}
                <FaArrowRight className={`${!isMobile && "ml-2"} h-4 w-4`} />
            </Button>
        </div>
    );

    return (
        <div className="space-y-4">
            {!(activeTab === "archived" && (archived ?? []).length === 0) && (
                <>
                    {!hideTypeControls && (
                        <h3 className="text-lg font-semibold text-primary">Select Media</h3>
                    )}
                    <span className="text-sm text-gray-500">
                        {(() => {
                            // Video-only context: forced by Video tab, video-only constraint, or YouTube selection.
                            const platformList = platform
                                ?.split(",")
                                .map((p) => p.trim().toLowerCase()) ?? [];
                            const platformsImageOnly =
                                platform === SocialPlatforms.Pinterest ||
                                platform === SocialPlatforms.Google ||
                                platformList.includes("pinterest") ||
                                platformList.includes("google") ||
                                platformList.includes("gmb");
                            if (isVideo || mediaConstraint === "video") {
                                return "Please select one video.";
                            }
                            if (
                                mediaConstraint === "image" ||
                                platformsImageOnly ||
                                postType === "Promotion"
                            ) {
                                return `Please select up to ${maxMedia} image${maxMedia > 1 ? "s" : ""}.`;
                            }
                            if (hideTypeControls) {
                                // The segmented control already names the side;
                                // the instruction must match it.
                                return activeResourceType === ResourceType.Video
                                    ? "Please select one video."
                                    : `Please select up to ${maxMedia} image${maxMedia > 1 ? "s" : ""}.`;
                            }
                            return `Please select up to ${maxMedia} image${maxMedia > 1 ? "s" : ""} or one video.`;
                        })()}
                    </span>
                    {platform === SocialPlatforms.Pinterest && (
                        <div className="bg-gray-100 p-3 rounded-md">
                            <p className="text-sm text-gray-600">
                                <strong className="text-primary">Note:</strong>{" "}
                                Pinterest favors unique images. We recommend uploading
                                fresh media for this post.
                            </p>
                        </div>
                    )}
                    {activeTab === "assets" && !hideTypeControls && (
                        <div className="flex justify-center">
                            <div className={isMobile ? "w-[80%]" : "w-[30%]"}>
                                <CustomSelect
                                    id="resource-type-select-dropdown"
                                    options={options}
                                    value={
                                        activeResourceType
                                            ? {
                                                  value: activeResourceType,
                                                  label: activeResourceType,
                                              }
                                            : null
                                    }
                                    onChange={(selectedOption) => {
                                        setActiveResourceType(
                                            selectedOption?.value as ResourceType
                                        );
                                    }}
                                    placeholder="Select Resource Type"
                                    isClearable={false}
                                    className="mb-6 w-full"
                                    bg="#F5F5F5"
                                />
                            </div>
                        </div>
                    )}
                    {activeTab === "promotionImages" && (
                        <div className="flex justify-center">
                            <div className={isMobile ? "w-[80%]" : "w-[30%]"}>
                                <CustomSelect
                                    id="promotion-type-select-dropdown"
                                    options={promotionTypeOptions}
                                    value={
                                        activePromotionType
                                            ? promotionTypeOptions.find(
                                                  (option) =>
                                                      option.value ===
                                                      activePromotionType
                                              )
                                            : null
                                    }
                                    onChange={(selectedOption) => {
                                        setActivePromotionType(
                                            selectedOption?.value as PromotionType
                                        );
                                    }}
                                    placeholder="Select Promotion Type"
                                    isClearable={false}
                                    className="mb-6 w-full"
                                    bg="#F5F5F5"
                                />
                            </div>
                        </div>
                    )}
                </>
            )}

            {showAssetsUploader && onAssetsUploaded && (
                <div className="my-4">
                    <AssetsUploader
                        key={type}
                        listingId={listingId || ""}
                        listingType={uploadsEnabled ? listingType : undefined}
                        onAssetsUploaded={handleAssetsUploadedWrapper}
                        assetType={
                            (activePromotionType as AssetType)
                                ? "Image"
                                : (activeResourceType as AssetType) || "Image"
                        }
                        type={
                            type === "asset"
                                ? activeResourceType === ResourceType.Video
                                    ? "assetVideo"
                                    : "assetImage"
                                : type === "video" || type === "assetVideo"
                                ? "assetVideo"
                                : type === "image" || type === "assetImage"
                                ? "assetImage"
                                : type === "archived"
                                ? "assetImage"
                                : type
                        }
                        uploadsEnabled={uploadsEnabled}
                        label={(type === "archived" || (postType === "Promotion" && activeTab === "images")) ? "Upload New Image" : undefined}
                        onPendingFilesChange={onPendingFilesChange}
                        onActiveUploadsChange={onActiveUploadsChange}
                    />
                </div>
            )}

            {combinedMedia.length > 0 && (
                <div className="mb-2 text-sm text-gray-600">
                    {getSelectedMediaText()}
                </div>
            )}
            <div className="mt-2 pt-2 pb-4 px-1">
                {isLoading ? (
                    <div className="flex items-center justify-center h-48">
                        <Spinner />
                    </div>
                ) : combinedMedia.length === 0 &&
                  (activeTab === "assets" || activeTab === "archived") ? (
                    <div className="flex items-center justify-center py-12 text-sm text-gray-400">
                        No media available.
                    </div>
                ) : (
                    <div className={cn("grid gap-2", isMobile ? "grid-cols-2" : "grid-cols-5")}>
                        {paginatedMedia.map((item) => renderMediaItem(item))}
                    </div>
                )}
            </div>

            {totalPages > 1 && renderPagination(currentPage, totalPages, setCurrentPage)}

            {combinedMedia.length > 0 && selectedMedia.length < minMedia && !hasVideoSelected && (
                <div className="text-red-500 text-sm">
                    Please select at least {minMedia}{" "}
                    {isVideo ||
                    (activeTab === "assets" &&
                        activeResourceType === ResourceType.Video)
                        ? "video"
                        : `image${maxMedia > 1 ? "s" : ""}`}{" "}
                    to continue.
                </div>
            )}
        </div>
    );
};

export default PostMediaSelector;
